/** Sort radios — pure helpers for GH4 (which options render, which one loads selected). */

import { isNonBlank } from "./is-non-blank.shared";

export interface SortOption {
  /** Stable identity, and the radio's value. */
  value: string;
  /** Content-managed wording (GH4 — "the content team sets which options
   *  appear"). Long labels wrap; nothing here truncates. */
  label: string;
}

/**
 * Cross-component contract, not a local choice. These two strings are what the organism root puts
 * in `searchParams` and what the grid's own grouping (GS1-GS3) has to switch on, so they are
 * exported rather than inlined.
 */
export const SORT_VALUE_AZ = "az";
export const SORT_VALUE_CATEGORY = "category";

/**
 * GS1/GS2's third sort option, not a third view — Channel Lineup has only `"signature" | "genre"`
 * views (`channel-lineup.tsx:27`); this is one more value for the Signature view's own "Sort by"
 * group.
 */
export const SORT_VALUE_NUMBER = "number";

/**
 * The three values above as a type, for the modules that bucket by sort
 * (`signature-grid.shared.ts`) rather than the ones that carry an author's spelling around.
 */
export type LineupSortValue =
  | typeof SORT_VALUE_AZ
  | typeof SORT_VALUE_CATEGORY
  | typeof SORT_VALUE_NUMBER;

/** The closed vocabulary, in GH4's own order. Exported so a guard's message can
 *  name what it would have accepted. */
export const LINEUP_SORT_VALUES: readonly LineupSortValue[] = [
  SORT_VALUE_AZ,
  SORT_VALUE_CATEGORY,
  SORT_VALUE_NUMBER
];

/**
 * Takes an already-normalised value — it does not trim or fold case itself, because the caller that
 * normalises is also the one that has to report the spelling it was given.
 */
export function isLineupSortValue(value: string): value is LineupSortValue {
  return (LINEUP_SORT_VALUES as readonly string[]).includes(value);
}

export interface PreparedSortOptions {
  /** The authored options that survive; empty means no sort control is drawn. */
  renderable: SortOption[];
  /** Dropped for a blank `value` or `label`. */
  malformed: number;
  /** Dropped for repeating a `value` already taken. */
  duplicates: string[];
  /** Nothing usable was authored. */
  usedDefaults: boolean;
}

/** GH4 — the authored options, minus anything malformed or duplicated. */
export function prepareSortOptions(options?: SortOption[]): PreparedSortOptions {
  const renderable: SortOption[] = [];
  const duplicates: string[] = [];
  const seen = new Set<string>();
  let malformed = 0;

  for (const option of options ?? []) {
    if (!isNonBlank(option?.value) || !isNonBlank(option?.label)) {
      malformed += 1;
      continue;
    }

    if (seen.has(option.value)) {
      duplicates.push(option.value);
      continue;
    }

    seen.add(option.value);
    renderable.push(option);
  }

  const usedDefaults = renderable.length === 0;

  return {
    renderable,
    malformed,
    duplicates,
    usedDefaults
  };
}

/** GH4's load-time selection, over options already through `prepareSortOptions`. */
export function resolveSelectedSort(
  options: SortOption[],
  selected?: string
): string | undefined {
  if (options.length === 0) {
    return undefined;
  }

  const authored = options.find(option => option.value === selected);
  return (authored ?? options[0]).value;
}
